import React from "react";
import { Alert, Badge, Card, Col, Form, Row, Spinner } from "react-bootstrap";
import InfoTooltip from "@core/components/InfoTooltip";
import {
    DeductibleAmountField,
    NO_ALLOCATION,
    NO_ALLOCATION_LABEL,
    fieldsForCalcType,
} from "@features/admin/finance/financeCalcTypes";
import { EMPTY_ROW_STATE, fieldLabel, withCalcType } from "@features/admin/finance/deductibleConfigUtils";
import {
    DeductibleEditorRow,
    DeductibleEditorValue,
    DeductibleRowErrors,
} from "@features/admin/finance/types/deductibleConfig";

export interface CalcTypeOption {
    calc_type_code: string;
    calc_type_name: string;
}

interface DeductibleConfigEditorProps {
    rows: DeductibleEditorRow[];
    value: DeductibleEditorValue;
    onChange: (next: DeductibleEditorValue) => void;
    calcTypes: CalcTypeOption[];
    errors?: DeductibleRowErrors;
    generalErrors?: string[];
    loading?: boolean;
    loadError?: string | null;
    disabled?: boolean;
}

const FIELD_INPUT: Record<DeductibleAmountField, { min: string; max?: string; step: string; placeholder: string }> = {
    rate: { min: "0", max: "100", step: "0.001", placeholder: "e.g. 10" },
    floor_amount: { min: "0", step: "0.01", placeholder: "e.g. 100.00" },
    cap_amount: { min: "0.01", step: "0.01", placeholder: "e.g. 500.00" },
    fixed_amount: { min: "0.01", step: "0.01", placeholder: "e.g. 200.00" },
};

/**
 * One row per deductible in list order: calc type ("No allocation" or an API calc type) and the
 * amount fields that type needs. Inactive deductibles are shown read-only with their configuration.
 */
const DeductibleConfigEditor: React.FC<DeductibleConfigEditorProps> = ({
    rows,
    value,
    onChange,
    calcTypes,
    errors = {},
    generalErrors = [],
    loading = false,
    loadError = null,
    disabled = false,
}) => {
    const setRow = (deductibleId: number, next: DeductibleEditorValue[number]) =>
        onChange({ ...value, [deductibleId]: next });

    return (
        <Card className="mb-4 border-0 bg-light" data-testid="deductible-config-editor">
            <Card.Body>
                <h6 className="fw-semibold mb-3 text-muted text-uppercase small">
                    Deductibles
                    <InfoTooltip text="How much of the income goes to each deductible. Choose No allocation to leave a deductible out. Whatever remains goes to the church." />
                </h6>

                {generalErrors.length > 0 && (
                    <Alert variant="danger" className="py-2 small" data-testid="deductible-general-errors">
                        {generalErrors.map((m) => <div key={m}>{m}</div>)}
                    </Alert>
                )}

                {loading ? (
                    <div className="d-flex align-items-center"><Spinner animation="border" size="sm" className="me-2" /><span>Loading deductibles…</span></div>
                ) : loadError ? (
                    <div className="text-danger small">{loadError}</div>
                ) : rows.length === 0 ? (
                    <div className="text-muted small">No active deductibles. Add them on the Deductibles page.</div>
                ) : (
                    rows.map((row) => {
                        const state = value[row.deductible_id] ?? EMPTY_ROW_STATE;
                        const rowErrors = errors[row.deductible_id] ?? {};
                        const rowDisabled = disabled || row.locked;
                        const fields = fieldsForCalcType(state.calc_type);
                        const selectId = `deductible_${row.deductible_id}_calc_type`;

                        return (
                            <div
                                key={row.deductible_id}
                                className="border-bottom pb-3 mb-3"
                                data-testid={`deductible-row-${row.deductible_id}`}
                            >
                                <div className="d-flex align-items-center gap-2 mb-2">
                                    <span className="fw-semibold">{row.name}</span>
                                    {row.locked && (
                                        <Badge bg="secondary" data-testid={`deductible-inactive-${row.deductible_id}`}>Inactive</Badge>
                                    )}
                                </div>
                                {row.locked && (
                                    <div className="text-muted small mb-2">
                                        This deductible is inactive. Its configuration is kept and cannot be changed.
                                    </div>
                                )}
                                <Row className="g-2">
                                    <Col xs={12} md={fields.length > 0 ? 4 : 12}>
                                        <Form.Group controlId={selectId}>
                                            <Form.Label className="small mb-1">Calculation Type</Form.Label>
                                            <Form.Select
                                                size="sm"
                                                value={state.calc_type}
                                                disabled={rowDisabled}
                                                isInvalid={!!rowErrors.calc_type || !!rowErrors.deductible_id}
                                                onChange={(e) => setRow(row.deductible_id, withCalcType(state, e.target.value as typeof state.calc_type))}
                                            >
                                                {!row.locked && <option value={NO_ALLOCATION}>{NO_ALLOCATION_LABEL}</option>}
                                                {calcTypes.map((ct) => (
                                                    <option key={ct.calc_type_code} value={ct.calc_type_code}>{ct.calc_type_name}</option>
                                                ))}
                                            </Form.Select>
                                            <Form.Control.Feedback type="invalid">
                                                {rowErrors.calc_type ?? rowErrors.deductible_id}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                    {fields.map((field) => {
                                        const input = FIELD_INPUT[field];
                                        return (
                                            <Col xs={12} md={Math.floor(8 / fields.length)} key={field}>
                                                <Form.Group controlId={`deductible_${row.deductible_id}_${field}`}>
                                                    <Form.Label className="small mb-1">
                                                        {fieldLabel(field)} <span className="text-danger">*</span>
                                                    </Form.Label>
                                                    <Form.Control
                                                        size="sm"
                                                        type="number"
                                                        min={input.min}
                                                        max={input.max}
                                                        step={input.step}
                                                        placeholder={input.placeholder}
                                                        value={state[field]}
                                                        disabled={rowDisabled}
                                                        isInvalid={!!rowErrors[field]}
                                                        onChange={(e) => setRow(row.deductible_id, { ...state, [field]: e.target.value })}
                                                    />
                                                    <Form.Control.Feedback type="invalid">{rowErrors[field]}</Form.Control.Feedback>
                                                </Form.Group>
                                            </Col>
                                        );
                                    })}
                                </Row>
                            </div>
                        );
                    })
                )}
            </Card.Body>
        </Card>
    );
};

export default DeductibleConfigEditor;
